import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../../utils/config";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { useUserContext } from "../../utils/UserContext";
import { formatNumero, formatDate } from "../../utils/Utilitats";

/**
 * mode: "nou" | "consultar" | "modificar" | "esborrar"
 * tipus: "pressupost" | "treball"
 * Chrome (capçalera/subnav) el posa Ofertes.js.
 */
function DetallsOferta({
  mode,
  tipus,
  entitat,
  oferta,
  ofertesList,
  onNavigate,
  onSave,
  onCancel,
}) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  TeclaEscEnter(onCancel, null);
  const { empresaSeleccionada } = useUserContext();

  const [dataOferta, setDataOferta] = useState("");
  const [proveidorId, setProveidorId] = useState("");
  const [proveidorOptions, setProveidorOptions] = useState([]);
  const [referenciaProv, setReferenciaProv] = useState("");
  const [descripcio, setDescripcio] = useState("");
  const [preuTotal, setPreuTotal] = useState(0);
  const [dataFiOferta, setDataFiOferta] = useState("");
  const [error, setError] = useState("");
  const [enviant, setEnviant] = useState(false);

  const resetForm = useCallback(() => {
    setDataOferta(new Date().toISOString().split("T")[0]);
    setProveidorId("");
    setReferenciaProv("");
    setDescripcio("");
    setPreuTotal(0);
    setDataFiOferta("");
    setError("");
  }, []);

  useEffect(() => {
    if (mode === "nou") {
      resetForm();
      return;
    }
    setDataOferta((oferta?.DataOferta || "").toString().slice(0, 10));
    setProveidorId(oferta?.Proveidors_idProveidors ? String(oferta.Proveidors_idProveidors) : "");
    setReferenciaProv(oferta?.ReferenciaProv || "");
    setDescripcio(oferta?.Descripcio || "");
    setPreuTotal(oferta?.PreuTotal || 0);
    setDataFiOferta((oferta?.DataFiOferta || "").toString().slice(0, 10));
    setError("");
  }, [oferta, mode, resetForm]);

  useEffect(() => {
    const fetchProveidors = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const response = await fetch(`${API_URL}/Proveidorsofertes.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const data = await response.json();
        if (data.success) setProveidorOptions(data.proveidors || []);
      } catch (err) {
        console.error("Error al carregar proveïdors:", err);
      }
    };
    fetchProveidors();
  }, [empresaSeleccionada]);

  const validarFormulari = () => {
    const errors = [];
    if (!dataOferta) errors.push("El camp Data oferta és obligatori.");
    if (!proveidorId) errors.push("Has de seleccionar un proveïdor.");
    if (!descripcio.trim()) errors.push("El camp Descripció és obligatori.");
    return errors;
  };

  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      const errors = validarFormulari();
      if (errors.length > 0) {
        setError(errors.join(" "));
        return;
      }
    }
    setError("");
    setEnviant(true);

    const ofertaData = {
      Pressupostos_idPressupostos: tipus === "pressupost" ? entitat?.idPressupostos : null,
      Treballs_idTreballs: tipus === "treball" ? entitat?.idTreballs : null,
      DataOferta: dataOferta,
      Proveidors_idProveidors: proveidorId ? Number(proveidorId) : null,
      ReferenciaProv: referenciaProv,
      Descripcio: descripcio,
      DataFiOferta: dataFiOferta || null,
    };

    try {
      if (mode === "nou") {
        const response = await fetch(`${API_URL}/Novaoferta.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ofertaData),
        });
        const data = await response.json();
        if (data.success) onSave?.(data.oferta);
        else setError(data.message || "Error en crear l'oferta.");
      } else if (mode === "modificar") {
        const response = await fetch(`${API_URL}/Modificaroferta.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...ofertaData, idOfertes: oferta?.idOfertes }),
        });
        const data = await response.json();
        if (data.success) onSave?.({ ...oferta, ...ofertaData });
        else setError(data.message || "Error en modificar l'oferta.");
      } else if (mode === "esborrar") {
        const response = await fetch(`${API_URL}/Esborraroferta.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idOfertes: oferta.idOfertes }),
        });
        const data = await response.json();
        if (data.success) onSave?.();
        else setError(data.message || "Error en esborrar l'oferta.");
      }
    } catch (err) {
      console.error("Error al submit:", err);
      setError("S'ha produït un error de connexió.");
    } finally {
      setEnviant(false);
    }
  };

  const camp = (label, content, mida = "mig", required = false) => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const valor = (txt) => <span className="dv2-value">{txt || "—"}</span>;
  const nomProveidor =
    oferta?.NomProveidor ||
    proveidorOptions.find((p) => String(p.idProveidors) === String(proveidorId))?.NomProveidor ||
    "";

  const idxNav =
    ofertesList && oferta
      ? ofertesList.findIndex((o) => Number(o.idOfertes) === Number(oferta.idOfertes))
      : -1;
  const potNavegar = mode === "consultar" && ofertesList && idxNav >= 0 && ofertesList.length > 1;
  const anarOferta = (delta) => {
    if (!potNavegar) return;
    const n = ofertesList.length;
    onNavigate?.(ofertesList[(idxNav + delta + n) % n]);
  };

  return (
    <>
      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          S'esborrarà l'oferta i els productes que en formen part. Aquesta acció no es pot desfer.
        </div>
      )}
      <div className="dv2-entity-body">
        {error ? <p className="dv2-hint">{error}</p> : null}
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Dades de l'oferta</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Data oferta",
              isReadOnly ? (
                valor(formatDate(dataOferta))
              ) : (
                <input
                  className="dv2-input"
                  type="date"
                  value={dataOferta}
                  onChange={(e) => setDataOferta(e.target.value)}
                  disabled={enviant}
                />
              ),
              "mig",
              true
            )}
            {camp(
              "Proveïdor",
              isReadOnly ? (
                valor(nomProveidor)
              ) : (
                <select
                  className="dv2-select"
                  value={proveidorId}
                  onChange={(e) => setProveidorId(e.target.value)}
                  disabled={enviant}
                >
                  <option value="">Selecciona…</option>
                  {proveidorOptions.map((prov) => (
                    <option key={prov.idProveidors} value={prov.idProveidors}>
                      {prov.NomProveidor}
                    </option>
                  ))}
                </select>
              ),
              "llarg",
              true
            )}
            {camp(
              "Referència proveïdor",
              isReadOnly ? (
                valor(referenciaProv)
              ) : (
                <input
                  className="dv2-input"
                  type="text"
                  value={referenciaProv}
                  onChange={(e) => setReferenciaProv(e.target.value)}
                  disabled={enviant}
                />
              ),
              "llarg"
            )}
            {camp(
              "Fi de validesa",
              isReadOnly ? (
                valor(formatDate(dataFiOferta))
              ) : (
                <input
                  className="dv2-input"
                  type="date"
                  value={dataFiOferta}
                  onChange={(e) => setDataFiOferta(e.target.value)}
                  disabled={enviant}
                />
              )
            )}
            {camp(
              "Descripció",
              isReadOnly ? (
                valor(descripcio)
              ) : (
                <textarea
                  className="dv2-textarea"
                  value={descripcio}
                  onChange={(e) => setDescripcio(e.target.value)}
                  disabled={enviant}
                />
              ),
              "ample",
              true
            )}
            {mode !== "nou" &&
              camp("Preu total", valor(`${formatNumero(preuTotal)} €`), "curt")}
          </div>
        </section>
      </div>
      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button
                type="button"
                className="dv2-btn dv2-recordNav-btn"
                onClick={() => anarOferta(-1)}
                title="Anterior"
              >
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {idxNav + 1} / {ofertesList.length}
              </span>
              <button
                type="button"
                className="dv2-btn dv2-recordNav-btn"
                onClick={() => anarOferta(1)}
                title="Següent"
              >
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel} disabled={enviant}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--danger"
                  onClick={handleSubmit}
                  disabled={enviant}
                >
                  Esborrar oferta
                </button>
              ) : (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--primary"
                  onClick={handleSubmit}
                  disabled={enviant}
                >
                  Desar
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default DetallsOferta;
